import { useState, useRef } from "react";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { ReactComponent as FayeIcon } from "../../icons/FayeBrand.svg";
import ConfirmationCodeInput, {
  ConfirmationCodeInputRef,
} from "../common/ConfirmationCodeInput";
import useIsMobile from "components/hooks/useIsMobile";
import FlexBox from "components/common/FlexBox";

interface TwoFactorOTPProps {
  onVerify: (code: string) => Promise<void>;
  onCancel: () => void;
  onResend: () => Promise<void>;
  method: "email" | "sms";
}

export default ({
  onVerify,
  onCancel,
  onResend,
  method,
}: TwoFactorOTPProps) => {
  const theme = useTheme();
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const codeInputRef = useRef<ConfirmationCodeInputRef>(null);

  const handleNext = async () => {
    if (code.length !== 6) {
      setError("Please enter the full 6-digit code");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onVerify(code);
    } catch (err: any) {
      const errorMessage =
        (typeof err.response?.data === "string" ? err.response.data : null) ||
        err.response?.data?.message ||
        err.message ||
        "Oops, that wasn't the right code. Please try again.";
      setError(errorMessage);
      setLoading(false);
      // Focus the last input so backspace works immediately
      setTimeout(() => {
        codeInputRef.current?.focusLastInput();
      }, 0);
    }
  };

  const handleResend = async () => {
    setError(null);
    try {
      await onResend();
      setCode("");
    } catch (err) {
      if (err instanceof Error && err.message) {
        setError(err.message);
      } else {
        setError("Failed to resend code. Please try again.");
      }
    }
  };

  const contactText = method === "email" ? "your email" : "your phone";
  const isMobile = useIsMobile();

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !loading) {
      handleNext();
    }
  };

  return (
    <Box
      onKeyDown={handleKeyDown}
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: isMobile ? "white" : theme.palette.background.default,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "start",
          justifyContent: "center",
          marginTop: isMobile ? "-10vh" : 0,
          gap: "20px",
          width: isMobile ? "100%" : "505px",
          height: isMobile ? "100vh" : "auto",
          padding: "24px 32px",
          backgroundColor: isMobile ? undefined : "#FFF",
          borderRadius: isMobile ? 0 : "24px",
          border: isMobile ? "none" : `1px solid ${theme.palette.border}`,
        }}
      >
        <FayeIcon
          style={{
            flexShrink: 0,
            width: "48px",
          }}
        />

        <FlexBox direction="column" gap="8px" flex={0}>
          <Typography
            variant="h2Serif"
            sx={{
              color: theme.palette.text.primary,
              alignSelf: "flex-start",
            }}
          >
            Enter confirmation code
          </Typography>

          <Typography
            variant="body"
            sx={{
              color: theme.palette.text.secondary,
              alignSelf: "flex-start",
              display: "inline",
            }}
          >
            We sent a one-time code to {contactText}. If it doesn't arrive in
            the next few minutes you can{" "}
            <Typography
              component="span"
              variant="bodyHeavy"
              sx={{
                color: theme.palette.primary.main,
                cursor: "pointer",
                whiteSpace: "nowrap",
                display: "inline",
              }}
              onClick={handleResend}
            >
              resend it
            </Typography>
            .
          </Typography>
        </FlexBox>

        <FlexBox
          direction="column"
          gap="8px"
          flex={0}
          sx={{ width: isMobile ? "100%" : "auto" }}
        >
          <ConfirmationCodeInput
            ref={codeInputRef}
            value={code}
            onChange={setCode}
            error={!!error}
            disabled={loading}
          />

          {error && (
            <Typography variant="body" sx={{ color: theme.palette.error.main }}>
              {error}
            </Typography>
          )}
        </FlexBox>

        <Box
          sx={{
            display: "flex",
            gap: "12px",
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          <Button variant="text" onClick={onCancel} disabled={loading}>
            Try another method
          </Button>

          <Button
            variant={code.length === 6 ? "contained" : "outlined"}
            onClick={handleNext}
          >
            {loading ? "Verifying..." : "Next"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
